<script lang="ts">
  // Responsive grid for KPI cards (or any equal tiles). Uses `cols` columns when
  // every tile gets at least `minWidth`; otherwise an even split (6 → 3, 4 → 2,
  // 5 → 3 …) so rows stay balanced; on narrow containers `minCols`.
  //   <StatGrid cols={4}>
  //     <Stat card … /> <Stat card … /> …
  //   </StatGrid>
  import type { Snippet } from 'svelte';

  interface Props {
    cols?: 1 | 2 | 3 | 4 | 5 | 6;
    /** Smallest tile width in px before the grid uses fewer columns. */
    minWidth?: number;
    /** Columns on the narrowest container (phones). */
    minCols?: 1 | 2;
    gap?: 'sm' | 'md' | 'lg';
    /** 'panel': one bordered block with hairlines between the tiles (use plain <Stat>,
        not card). Calmer than a row of separate cards. */
    variant?: 'cards' | 'panel';
    ariaLabel?: string;
    class?: string;
    children: Snippet;
  }
  let { cols = 4, minWidth = 180, minCols = 2, gap = 'md', variant = 'cards', ariaLabel, class: cls = '', children }: Props = $props();

  let width = $state(0);
  const columns = $derived.by(() => {
    if (!width) return cols;
    const fits = (n: number) => width >= n * minWidth + (n - 1) * 16;
    if (fits(cols)) return cols;
    const half = Math.ceil(cols / 2);
    if (half > minCols && fits(half)) return half;
    return Math.min(cols, minCols);
  });
</script>

<div class="stat-grid-wrap {cls}" bind:clientWidth={width}>
  <div
    class="stat-grid gap-{gap} {variant}"
    role={ariaLabel ? 'group' : undefined}
    aria-label={ariaLabel}
    style:--cols={columns}
  >
    {@render children()}
  </div>
</div>

<style>
  .stat-grid-wrap { min-width: 0; }
  .stat-grid { display: grid; gap: var(--space-4); grid-template-columns: repeat(var(--cols), minmax(0, 1fr)); }
  .gap-sm { gap: var(--space-2); }
  .gap-lg { gap: var(--space-6); }
  /* Hairlines come from the 1px gap showing the border colour behind the tiles. */
  .panel { gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; }
  .panel > :global(*) { background: var(--surface); padding: var(--card-pad); }
</style>
